import { Deserializer } from 'jsonapi-serializer';
import { REPORTS_FILTER_DATE_TIME_DATE_RANGE, REPORTS_FILTER_DATE_TIME_SPECIFIC_DATE } from '../constants/element-type';
import { FILTER_DATE, FILTER_SITES, FILTER_TIME } from '../constants/filter-types';
import { CREATED, INTERNAL_SERVER_ERROR, NO_CONTENT, OK } from '../constants/response-status-codes';
import { DataFilterPreference } from '../models/DateFilterPreference';
import { genericError } from '../models/GenericError';
import { IPAddressesPreference, IPAddressesPreferencetWrapper } from '../models/IPAddressesPreference';
import { ListQuery } from '../models/ListQuery';
import { MultiSelectOption } from '../models/MultiSelectOption';
import { Name } from '../models/Name';
import { UserPreferenceFilter } from '../models/UserPreferenceFilter';
import { getTodayDate, isValidDate } from '../utilities/DateUtils';
import { APIConnector } from "./api/APIConnector";
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { createPagination } from './utilities/paginationBuilder';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';
import { deleteSettingData } from './settingDataService';
import { getFormattedTime, getTime } from './utilities/dateFormatting';
import { filterPreferencePattern, findMatchingAttribute, namePattern } from '../utilities/regex';

const apiConnector = new APIConnector();

const UserPreferenceType = {
    DEFAULT: 1,
    FILTER: 2
}

export const getUserColumnsByView = async (userLoginId, menuDetailId, reportId, settingId): Promise<any> => {
    try {
        const includes = `userLogin,userPreferenceType,menuDetail,settingType,report`;
        let filters = `menuDetail.settingType.id==${menuDetailId};userPreferenceType.id==1;userLogin.id==${userLoginId}`;
        filters = reportId ? `${filters};report.id==${reportId}` : filters;
        filters = settingId ? `${filters};setting.id==${settingId}` : filters;
        const userPreferenceFields = 'fields[userPreference]=menuDetail,userPreferenceType,settingType';
        const menuDetailFields = 'fields[menuDetail]=trnName';
        const url = `userPreference?include=${includes}&filter=${filters}&${userPreferenceFields}&${menuDetailFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const rows = data.map((preference) => {
                    return {
                        id: preference['id'],
                        menuId: preference['menu-detail']['id'],
                        menuName: preference['menu-detail']['trn-name'],
                        preferenceType: preference['user-preference-type']['id'],
                        columnId: preference['setting-type']['id'],
                    };
                });

                return resolve(rows)
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeUserPreferenceFilters = async (userPreferenceFilter: UserPreferenceFilter) => {
    try {
        const userPreference: UserPreference = {
            menuSettingTypeId: userPreferenceFilter.currentMenuDetail,
            columnId: userPreferenceFilter.filterId,
            userLoginId: userPreferenceFilter.userLoginId,
            userPreference: userPreferenceFilter.userPreference,
            reportId: userPreferenceFilter.reportId,
            settingId: userPreferenceFilter.settingId
        }

        const result: any = await storeUserPreferenceColumn(userPreference, UserPreferenceType.FILTER);

        return new Promise((resolve, reject) => {
            if (result && result.success) {
                const settingDataPromises = userPreferenceFilter.filterData.map((filterData) => {
                    let newDynamicObject = {};
                    newDynamicObject[filterData.filterTypeName] = {
                        data: {
                            type: filterData.filterTypeName,
                            id: filterData.value
                        }
                    };
                    const sdValstring = filterData.value;

                    let settingData: SettingData = {
                        value: sdValstring,
                        userPreference: result.id,
                        dynamicObject: {},
                        eSysnameLookup: undefined,
                        filterSettingTypeId: undefined
                    };

                    if (userPreferenceFilter.filterId === FILTER_DATE || userPreferenceFilter.filterId === FILTER_TIME) {
                        settingData = {
                            ...settingData,
                            eSysnameLookup: filterData.elementSysname,
                            dynamicObject: {
                                report: {
                                    data: {
                                        type: 'report',
                                        id: userPreferenceFilter.reportId
                                    }
                                }
                            }
                        }
                    } else {
                        settingData = {
                            ...settingData,
                            filterSettingTypeId: userPreferenceFilter.filterId,
                            dynamicObject: { ...newDynamicObject },
                        };
                    }

                    return storeSettingData(settingData)
                });
                // resolve after processing all setting data promises (how do we recover when a setting data insert fails?).
                Promise.all(settingDataPromises)
                    .then((data: any) => {
                        // Should remove the if statement because it doesn't make sense 
                        if (result.filterId === FILTER_DATE || result.filterId === FILTER_TIME) {
                            return resolve({ ...result, settingDataId: data[0].id })
                        }

                        return resolve({ ...result })
                    });
            }
            else {
                reject(result);
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

interface UserPreference {
    menuSettingTypeId;
    userLoginId;
    columnId;
    userPreference;
    reportId?: string;
    settingId?: string;
}

export const storeUserPreferenceColumn = async (userPreference, userPreferenceType = 1) => {
    try {
        const reportRelationship = userPreference.reportId ?
            {
                report: {
                    data: {
                        type: "report",
                        id: userPreference.reportId
                    }
                }
            }
            : {};

        const newRecord = {
            data: {
                type: "userPreference",
                attributes: {
                    menuSettingTypeId: userPreference.menuSettingTypeId,
                    settingId: userPreference.settingId
                },
                relationships: {
                    userPreferenceType: {
                        data: {
                            type: "userPreferenceType",
                            id: userPreference.userPreference ? userPreference.userPreference : userPreferenceType // new one to create
                        }
                    },
                    userLogin: {
                        data: {
                            type: "userLogin",
                            id: userPreference.userLoginId
                        }
                    },
                    settingType: {
                        data: {
                            type: "settingType",
                            id: userPreference.columnId
                        }
                    },
                    ...reportRelationship
                }
            }
        }

        const url = `userPreference`;
        let response: Response;

        await apiConnector.sendData(url, true, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise(async (resolve, reject) => {
            if (response) {
                const content = response.status != NO_CONTENT ? await response.json() : null;

                if (content.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                }
                new Deserializer().deserialize(content, (error, newUserPreference) => {
                    if (error) {

                        return reject({ status: response.status, id: newUserPreference.id, success: false, message: genericError });
                    }

                    return resolve({ id: newUserPreference.id, success: true, filterId: userPreference.columnId, ...newUserPreference });
                })
                return;
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: userPreference.id, success: false, message: genericError });

        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteUserPreference = async (id: string): Promise<{ id: string, success: boolean }> => {
    const url = `userPreference/${id}`;
    const userLogin = {
        data: {
            type: "userPreference",
            id: id
        }
    }
    const response = await apiConnector.deleteData(url, userLogin)

    return new Promise((resolve, reject) => {
        if (response.status === NO_CONTENT) {
            resolve({ id: id, success: true })
        }
        reject({ status: response.status, id: id, success: false });
    })
}

interface SettingData {
    value;
    userPreference;
    filterSettingTypeId;
    dynamicObject;
    eSysnameLookup;
}

export const storeSettingData = async (settingData) => {
    try {
        const newRecord = {
            data: {
                id: settingData.settingDataId,
                type: "settingData",
                attributes: {
                    sdValstring: settingData.value,
                    filterSettingTypeId: settingData.filterSettingTypeId,
                    eSysnameLookup: settingData.eSysnameLookup
                },
                relationships: {
                    ...settingData.dynamicObject,
                    userPreference: {
                        data: {
                            type: 'userPreference',
                            id: settingData.userPreference
                        }
                    }
                }

            }
        }

        const isRecordNew = settingData.settingDataId ? false : true;
        const url = `settingData${isRecordNew ? `` : `/${settingData.settingDataId}`}`;
        let response: Response;

        await apiConnector.sendData(url, isRecordNew, newRecord).then(data => {
            response = data;
        }).catch(error => {
            console.log('Save Login error: ', error);
        });

        return new Promise(async (resolve, reject) => {
            if (response) {
                if (isRecordNew) {
                    const content = await response.json();

                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, newUserPreference) => {
                        if (error) {

                            return reject({ status: response.status, id: newUserPreference.id, success: false, message: genericError });
                        }
                        return resolve({ id: newUserPreference.id, success: true, ...newUserPreference });
                    })
                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ settingDataId: settingData.settingDataId, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, settingDataId: settingData.settingDataId, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });

            }

            return reject({ status: INTERNAL_SERVER_ERROR, settingDataId: settingData.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

interface FilterPreference {
    filterId;
    preferenceId;
    selected: MultiSelectOption[];
}

const buildFilterPreference = userPreference => {
    const newFilter: FilterPreference = {
        filterId: userPreference['setting-type']['id'],
        preferenceId: userPreference['id'],
        selected: userPreference['setting-type']['id'] === FILTER_DATE || userPreference['setting-type']['id'] === FILTER_TIME ? [{
            label: userPreference['setting-datas'].length > 0 ? userPreference['setting-datas'][0]['sd-valstring'] : userPreference['setting-type']['id'] === FILTER_DATE ? getTodayDate() : getFormattedTime(getTime(new Date())),
            value: userPreference['setting-datas'].length > 0 ? userPreference['setting-datas'][0]['sd-valstring'] : userPreference['setting-type']['id'] === FILTER_DATE ? getTodayDate() : getFormattedTime(getTime(new Date())),
            settingDataId: userPreference['setting-datas'].length > 0 ? userPreference['setting-datas'][0]['id'] : "",
            elementSysname: userPreference['setting-datas'].length > 0 ? userPreference['setting-datas'][0]['element']['e-sysname'] : ""
        }]
            : userPreference['setting-datas'].reduce((selectedItems, settingData) => {
                // create a tuple (which is represented in javascript by an array)
                //   for each attribute of the setting data.
                const nonEmptyData = findMatchingAttribute(settingData, filterPreferencePattern);

                if (nonEmptyData) {
                    // repeat the steps above to get the name element for the filter option label.
                    const filterLabel = findMatchingAttribute(nonEmptyData[1], namePattern);

                    if (filterLabel) {
                        const newSelectedItem: MultiSelectOption = {
                            label: filterLabel[1] as string,
                            value: settingData['sd-valstring']
                        };
                        return [...selectedItems, newSelectedItem];
                    }
                }
                return selectedItems;
            }, new Array(0)),
    };
    return newFilter;
};

export const getUserFilterPreference = async (userLoginId, currentMenuDetailId: string, reportId?: string, settingId?: string): Promise<FilterPreference[]> => {
    try {
        let includes = "userLogin,userPreferenceType,menuDetail,settingType,settingDatas.accessTimePattern,settingDatas.area,settingDatas.eventType,";
        includes += "settingDatas.building,settingDatas.profile,settingDatas.deviceImprox,settingDatas.zone,settingDatas.location,settingDatas.company,";
        includes += "settingDatas.timePattern,settingDatas.site,settingDatas.department,report,settingDatas.element";
        const userPreferenceFields = "fields[userPreference]=settingType,settingDatas";
        const settingDataFields = "fields[settingData]=sdValstring,element,department,company,site,accessTimePattern,zone,area,location,profile,deviceImprox";
        const departmentFields = "fields[department]=deptName";
        const companyFields = "fields[company]=compName";
        const siteFields = "fields[site]=siteName";
        const accessTimePatternFields = "fields[accessTimePattern]=atpName";
        const zoneFields = "fields[zone]=zoneName";
        const areaFields = "fields[area]=areaName";
        const locationFields = "fields[location]=locName";
        const profileFields = "fields[profile]=trnName";
        const deviceFields = "fields[deviceImprox]=devName";
        const elementFields = "fields[element]=eSysname";
        const menuFilter = `menuDetail.settingType.id==${currentMenuDetailId};userPreferenceType.id==2`;
        const userFilter = `userLogin.id==${userLoginId}`;
        let filters = `${menuFilter};${userFilter}`;
        const reportFilter = reportId ? `${filters};report.id==${reportId}` : filters;    
        filters = settingId ? `${reportFilter};setting.id==${settingId}` : reportFilter;
        let url = `userPreference?include=${includes}&${userPreferenceFields}&${settingDataFields}&${departmentFields}&${companyFields}&${siteFields}&`;
        url += `${accessTimePatternFields}&${zoneFields}&${areaFields}&${locationFields}&${profileFields}&${deviceFields}&${elementFields}&filter=${filters}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }
                const newFilters: FilterPreference[] = data.map(buildFilterPreference);
                return resolve(newFilters);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getUserFiltersByView = async (userLoginId, menuDetailId, reportId?): Promise<any[]> => {
    try {
        const reportFilter = reportId ? `;report.id==${reportId}` : ""; //Required for getting reports preference
        const include = 'include=userLogin,userPreferenceType,menuDetail,settingType,report,settingDatas.accessTimePattern,settingDatas.area,settingDatas.eventType,settingDatas.building,settingDatas.profile,settingDatas.deviceImprox,settingDatas.zone,settingDatas.location,settingDatas.company,settingDatas.timePattern,settingDatas.site,settingDatas.department,settingDatas.element';
        const filter = `&filter=menuDetail.settingType.id==${menuDetailId};userPreferenceType.id==2;userLogin.id==${userLoginId}${reportFilter}`;
        const userPreferenceFields = 'fields[userPreference]=menuDetail,userPreferenceType,settingType,settingDatas';
        const menuDetailFields = 'fields[menuDetail]=trnName';
        const url = `userPreference?${include}${filter}&${userPreferenceFields}&${menuDetailFields}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const rows = data.map((preference) => {
                    return {
                        id: preference['id'],
                        menuId: preference['menu-detail']['id'],
                        menuName: preference['menu-detail']['trn-name'],
                        preferenceType: preference['user-preference-type']['id'],
                        filterId: preference['setting-type']['id'],
                        settingDatas: preference['setting-datas']
                    };
                });
                return resolve(rows)
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getUserDataFiltersByLoginId = async (userLoginId: string, listParameters?: ListQuery): Promise<any> => {
    try {
        const userPreferenceFields = '&fields[userPreference]=settingType';
        const settingTypeFields = '&fields[settingType]=trnName';
        const url = `userPreference?page[totals]&include=userLogin,userPreferenceType,settingType,menuDetail&filter=userPreferenceType.id==5;userLogin.id==` + userLoginId + userPreferenceFields + settingTypeFields;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {

            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail), success: false })
            }
            new Deserializer().deserialize(json, (err: any, response: any[]) => {
                if (err) {
                    return reject(err);
                }
                const rows = response.map((preference) => {
                    return {
                        id: preference['id'],
                        name: preference['setting-type']['trn-name'],
                    };
                });

                return resolve({ rows: rows, pagination: pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const buildSettingDataObject = (settingDatas) => {
    const buildSettingData = [] as any;
    settingDatas.filter((newSettingData: any) => {
        const newRecord = {
            sdValstring: newSettingData,
        }
        buildSettingData.push(newRecord)
    });
    return buildSettingData;
}

const generateSettingDataName = (columnId: string) => {
    if (columnId === FILTER_SITES) {
        return 'site';
    }
    return '';
}


export const savePreference = async (userPreference, createUserLoginPrefrence = true): Promise<any> => {
    if (createUserLoginPrefrence) {
        return storeUserPreferenceForLogins(userPreference);
    }
    else {
        return updateLoginPreference(userPreference);
    }
}

const updateLoginPreference = (userPreference) => {
    const newSelectedIds = userPreference.settingDatas.filter(value => value !== "")
    const oldSelectedIds = userPreference.oldSelectedFilters.filter(value => value !== "")

    const toBeRemoved = oldSelectedIds.filter(value => {
        if (!!!newSelectedIds.includes(value)) {
            return value
        }
    });

    const toBeAdded = newSelectedIds.filter(value => {
        if (!!!oldSelectedIds.includes(value)) {
            return value
        }
    });

    if (toBeRemoved.length > 0) {
        removeUserLoginPreferences(userPreference, toBeRemoved)
    }

    if (toBeAdded.length > 0) {
        updateUserLoginPreference(userPreference, toBeAdded)
    }
}

export const removeUserLoginPreferences = (userPreference, toBeRemoved) => {
    const settingDataObjects = userPreference.settingDataObjects;
    const removedUserFilterPreferences = toBeRemoved.map((filter) => {
        settingDataObjects.map(async (settingDataObject) => {
            if (filter === settingDataObject.value) {
                await deleteSettingData(settingDataObject.id);
            }
        })
    });
    return Promise.allSettled(removedUserFilterPreferences);
}

export const updateUserLoginPreference = async (userPreference, toBeAdded) => {
    try {
        const generatedSettingDataName = generateSettingDataName(userPreference.columnId);
        const addedFilterPreferences = toBeAdded.map(async (filter) => {

            var settingData = {};
            settingData[generatedSettingDataName] = {
                data: {
                    type: generatedSettingDataName,
                    id: filter + ''
                }
            };
            await storeSettingData({
                value: filter + '',
                userPreference: userPreference.id,
                filterSettingTypeId: userPreference.columnId,
                dynamicObject: {
                    ...settingData
                }
            })
        })
        return Promise.allSettled(addedFilterPreferences);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeUserPreferenceForLogins = async (userPreference) => {
    try {
        const userPreferenceId = 5;
        const newRecord = {
            type: "userPreference",
            relationships: {
                userPreferenceTypeId: userPreferenceId,
                userLoginId: parseInt(userPreference.userLoginId),
                settingTypeId: parseInt(userPreference.columnId),
                settingData: buildSettingDataObject(userPreference.settingDatas)
            }
        }

        const url = '';
        const settingResponse = await apiConnector.sendData(url, true, newRecord, true)

        return new Promise(async (resolve, reject) => {
            if (settingResponse) {
                if (settingResponse.status === OK
                    || settingResponse.status === CREATED) {
                    try {
                        const settingJson = await settingResponse.json()
                        return resolve({ id: settingJson['attributes']?.['id'], success: true })
                    }
                    catch (exception) {
                        return reject({ status: settingResponse.status, id: userPreference.id, message: genericError, success: false });
                    }
                }
                else {
                    if (settingResponse.body) {
                        if (settingResponse.statusText) {
                            const content = await settingResponse.json();
                            return reject({ status: settingResponse.status, id: userPreference.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
                        }
                        else {
                            return reject({ status: settingResponse.status, id: userPreference.id, message: genericError, success: false });
                        }
                    }
                    else {
                        return reject({ status: settingResponse.status, id: userPreference.id, message: genericError, success: false });
                    }
                }
            }
            else {
                return reject({ status: INTERNAL_SERVER_ERROR, id: userPreference.id, message: genericError, success: false });
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}


export const getUserFiltersByIdForUserLogin = async (id: string): Promise<DataFilterPreference> => {
    try {
        const userPreferenceFields = 'fields[userPreference]=userPreferenceType,settingType,settingDatas,userLogin';
        const settingDatasFields = 'fields[settingData]=sdValstring';
        const url = `userPreference/${id}?include=userLogin,userPreferenceType,settingType,settingDatas,settingDatas.accessTimePattern,settingDatas.area,settingDatas.eventType,settingDatas.building,settingDatas.profile,settingDatas.deviceImprox,settingDatas.zone,settingDatas.location,settingDatas.company,settingDatas.timePattern,settingDatas.site,settingDatas.department&${userPreferenceFields}&${settingDatasFields}`
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const preference: any = {
                    id: data['id'],
                    preferenceTypeId: data['user-preference-type']['id'],
                    columnId: data['setting-type']['id'],
                    settingDatas: buildSelectedDataFiltersForPreference(data['setting-datas']),
                    type: data['setting-type']['id'],
                    userLoginId: data['user-login']['id'],
                    oldSelectedFilters: buildSelectedDataFiltersForPreference(data['setting-datas']),
                    settingDataObjects: buildSettingDataIds(data['setting-datas'])
                }
                return resolve(preference)
            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const buildSelectedDataFiltersForPreference = (settingDatas: any) => {
    const sdValstringValues = settingDatas.map((settingData) => {
        return settingData['sd-valstring']
    });
    return sdValstringValues;
}

const buildSettingDataIds = (settingDatas: any) => {
    const sdValstringValues = settingDatas.map((settingData) => {
        return { id: settingData['id'], value: settingData['sd-valstring'] }
    });
    return sdValstringValues;
}

export const getSettingDataForUserLogin = async (userPreferenceId: string, columnId: string): Promise<Name[]> => {
    try {
        const settingDataFields = 'fields[settingData]=sdValstring';
        const url = `settingData?include=userPreference.settingType&filter=userPreference.id==${userPreferenceId},userPreference.settingType.id==${columnId}&${settingDataFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {

            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const settingDatas: Name[] = data.map(settingData => { return { label: settingData['sd-valstring'], value: settingData['id'] } });
                return resolve(settingDatas);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const saveUserFilterPreferenceForIPRanges = async (userPreferenceFilter: UserPreferenceFilter) => {
    /**
     * Check if user has already set a preference for IP ranges if we do just save the setting data
     * otherwise create a new user preference and save the setting data
     */
    const userFilterPreferenceForIpRange = await getUserPreferencesForIPRanges();

    if (userFilterPreferenceForIpRange.rows.length > 0) {
        userPreferenceFilter.filterData.map((filterData) => {
            const settingData: any = {
                value: filterData.value,
                userPreference: userFilterPreferenceForIpRange.rows[0].value,
                eSysnameLookup: filterData.elementSysname,
            };
            return storeSettingData(settingData)
        });
    }
    else {
        return await storeUserPreferenceFilterForIPRanges(userPreferenceFilter);
    }
}

export const storeUserPreferenceFilterForIPRanges = async (userPreferenceFilter: UserPreferenceFilter) => {
    try {
        const userPreference: UserPreference = {
            menuSettingTypeId: userPreferenceFilter.currentMenuDetail,
            columnId: userPreferenceFilter.filterId,
            userLoginId: userPreferenceFilter.userLoginId,
            userPreference: userPreferenceFilter.userPreference
        }

        const result: any = await storeUserPreferenceColumn(userPreference, UserPreferenceType.FILTER);

        return new Promise((resolve, reject) => {
            if (result && result.success) {
                const settingDataPromises = userPreferenceFilter.filterData.map((filterData) => {
                    const settingData: any = {
                        value: filterData.value,
                        userPreference: result.id,
                        filterSettingTypeId: userPreferenceFilter.filterId,
                    };
                    return storeSettingData(settingData)
                });
                // resolve after processing all setting data promises (how do we recover when a setting data insert fails?).
                Promise.all(settingDataPromises)
                    .then((data: any) => {
                        if (result.filterId === FILTER_DATE) {
                            resolve({ ...result, settingDataId: data[0].id })
                        }

                        resolve({ ...result, })
                    });
            }
            else {
                reject(result);
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getUserPreferencesForIPRanges = async (listParameters?: ListQuery): Promise<any> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const filterRowType = "filter=userPreferenceType.id==7;userLogin.id==1";
        const updatedFilters = filter.length > 0
            ? filter.replace("filter=", `${filterRowType};`)
            : `&${filterRowType}`
        const url = `userPreference?page[totals]&include=userLogin,userPreferenceType,settingType,menuDetail` + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, updatedFilters);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {

            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const settingDatas: Name[] = data.map(settingData => { return { label: settingData['sd-valstring'], value: settingData['id'] } });
                return resolve({ rows: settingDatas, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}


export const getSystemUserIpRanges = async (listParameters?: ListQuery): Promise<IPAddressesPreferencetWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const filterRowType = "filter=userPreference.userPreferenceType.id==7,userLogin.id==1";
        const updatedFilters = filter.length > 0
            ? filter.replace("filter=", `${filterRowType};`)
            : `&${filterRowType}`
        const settingDataFields = '&fields[settingData]=sdValstring';
        const url = `settingData?include=userPreference.settingType` + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, updatedFilters) + settingDataFields;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const rows: IPAddressesPreference[] = data.map((preference) => {
                    const ipAddresses = preference['sd-valstring'].split(';');
                    return {
                        id: preference['id'],
                        startIpAddress: ipAddresses[0],
                        endIpAddress: ipAddresses[1],
                    };
                });
                return resolve({ rows: rows, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

